import { Checkbox, clx, Label, RadioGroup, Text } from "@medusajs/ui"
import { AREA_HELP, AREA_LABELS, AREAS, VIEW_ONLY_AREAS, type Permission, type Role } from "@/lib/types"

export type Access = { role: Exclude<Role, "owner">; permissions: Permission[] }

/**
 * Shopify-style access picker: "Full permissions" or "Selected permissions",
 * then View / Edit per area. Edit implies View; removing View removes Edit.
 * Boxes for permissions the signed-in person doesn't hold are disabled,
 * because staff can only pass on what they have.
 */
export function AccessEditor({
  value,
  onChange,
  canGiveFull,
  grantable,
  disabled,
}: {
  value: Access
  onChange: (v: Access) => void
  canGiveFull: boolean
  grantable: Set<Permission>
  disabled?: boolean
}) {
  const has = (p: Permission) => value.permissions.includes(p)
  const toggle = (p: Permission, on: boolean) => {
    let next = value.permissions.filter((x) => x !== p)
    const [area, access] = p.split(":")
    if (on) {
      next.push(p)
      if (access === "write") next.push(`${area}:read` as Permission)
    } else if (access === "read") {
      next = next.filter((x) => x !== `${area}:write`)
    }
    onChange({ ...value, permissions: [...new Set(next)] })
  }

  return (
    <div className="flex flex-col gap-y-4">
      <RadioGroup
        value={value.role}
        onValueChange={(r) => onChange({ ...value, role: r as Access["role"] })}
        className="flex flex-col gap-y-3"
        disabled={disabled}
      >
        <div className="flex items-start gap-x-3">
          <RadioGroup.Item value="admin" id="role-admin" disabled={disabled || !canGiveFull} />
          <div className="flex flex-col">
            <Label htmlFor="role-admin" weight="plus" className={clx(!canGiveFull && "text-ui-fg-disabled")}>
              Full permissions
            </Label>
            <Text size="small" className="text-ui-fg-subtle">
              Everything in the admin, including staff and store settings. {!canGiveFull && "Only a store owner can give this."}
            </Text>
          </div>
        </div>
        <div className="flex items-start gap-x-3">
          <RadioGroup.Item value="staff" id="role-staff" />
          <div className="flex flex-col">
            <Label htmlFor="role-staff" weight="plus">
              Selected permissions
            </Label>
            <Text size="small" className="text-ui-fg-subtle">
              Only the areas you choose below.
            </Text>
          </div>
        </div>
      </RadioGroup>

      {value.role === "staff" && (
        <div className="overflow-hidden rounded-lg border border-ui-border-base">
          <div className="grid grid-cols-[1fr_64px_64px] items-center bg-ui-bg-subtle px-4 py-2">
            <Text size="xsmall" weight="plus" className="text-ui-fg-subtle">
              Area
            </Text>
            <Text size="xsmall" weight="plus" className="text-center text-ui-fg-subtle">
              View
            </Text>
            <Text size="xsmall" weight="plus" className="text-center text-ui-fg-subtle">
              Edit
            </Text>
          </div>
          {AREAS.map((a) => (
            <div key={a} className="grid grid-cols-[1fr_64px_64px] items-center border-t border-ui-border-base px-4 py-3">
              <div className="flex flex-col pr-4">
                <Text size="small" weight="plus">
                  {AREA_LABELS[a]}
                </Text>
                <Text size="xsmall" className="text-ui-fg-subtle">
                  {AREA_HELP[a]}
                </Text>
              </div>
              {(["read", "write"] as const).map((acc) => {
                if (acc === "write" && VIEW_ONLY_AREAS.includes(a)) return <span key={acc} className="text-center text-ui-fg-muted" aria-hidden="true">–</span>
                const p = `${a}:${acc}` as Permission
                const blocked = !grantable.has(p) && !has(p)
                return (
                  <div key={acc} className="flex justify-center">
                    <Checkbox
                      checked={has(p)}
                      disabled={disabled || blocked}
                      onCheckedChange={(c) => toggle(p, c === true)}
                      aria-label={`${AREA_LABELS[a]}: ${acc === "read" ? "view" : "edit"}`}
                    />
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** "Full permissions", "3 permissions", … for staff lists. */
export function accessSummary(role: Role, granted: Permission[]): string {
  if (role === "owner") return "Store owner"
  if (role === "admin") return "Full permissions"
  const areas = new Set(granted.map((p) => p.split(":")[0]))
  if (areas.size === 0) return "No permissions"
  return `${areas.size} area${areas.size === 1 ? "" : "s"}`
}
